/**
 * Клиент справочников (E3, «Настройки»): производственный календарь и ключевая ставка ЦБ.
 * Доступен только администратору — сервер проверяет право `reference:manage` на каждом маршруте
 * (матрица 4.3). Пути относительные (`/api/...`), сессия — cookie HttpOnly (Р-5), токенов в JS нет.
 *
 * Формы согласованы с сервером по значениям (CalendarDayView / KeyRateView из
 * apps/api/src/references/store.ts). Ставка ЦБ передаётся в СОТЫХ ДОЛЯХ ПРОЦЕНТА (целое,
 * 16 % → 1600) — конвертацию из «16,5 %» делают хелперы ниже (без float в транспорте, Р-3).
 */

export type CalendarDayType = "holiday" | "working";

/** День производственного календаря — ответ GET /api/references/calendar. */
export interface CalendarDay {
  readonly id: string;
  readonly date: string; // ГГГГ-ММ-ДД
  readonly type: CalendarDayType;
  readonly note: string | null;
}

/** Запись ключевой ставки ЦБ — ответ GET /api/references/key-rates. */
export interface KeyRate {
  readonly id: string;
  readonly from: string; // ГГГГ-ММ-ДД
  readonly rate: number; // сотые доли процента (16 % → 1600)
  readonly note: string | null;
}

export const DAY_TYPE_LABELS: Record<CalendarDayType, string> = {
  holiday: "Нерабочий",
  working: "Рабочий выходной",
};

/**
 * «16,5» / «16.5» / «16» → сотые доли процента (1650 / 1600). `null` — формат неверный.
 * Разбираем строку посимвольно (без умножения float): целая часть · 100 + две дробные цифры.
 */
export function parsePercentToHundredths(input: string): number | null {
  const s = input.trim().replace(/\s/g, "").replace("%", "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [intPart = "0", fracPart = ""] = s.split(".");
  const frac = (fracPart + "00").slice(0, 2);
  return parseInt(intPart, 10) * 100 + parseInt(frac, 10);
}

/** Сотые доли процента → строка процента: 1650 → «16,5», 1600 → «16», 1625 → «16,25». */
export function formatHundredthsAsPercent(rate: number): string {
  const whole = Math.floor(rate / 100);
  const frac = rate % 100;
  if (frac === 0) return String(whole);
  const fracStr = String(frac).padStart(2, "0").replace(/0$/, "");
  return `${whole},${fracStr}`;
}

/** Успех со значением либо типизированная причина отказа с готовым сообщением для UI. */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly reason: "unauthorized" | "forbidden" | "conflict" | "validation" | "network";
      readonly message: string;
    };

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

interface ErrorBody {
  error?: string;
  message?: string;
}

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as ErrorBody | null;
  const message = body?.message;
  if (res.status === 403) {
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  }
  if (res.status === 409) {
    return { ok: false, reason: "conflict", message: message ?? "Конфликт данных." };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

async function getJson<T>(url: string, key: string): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  return { ok: true, value: (body?.[key] ?? []) as T };
}

async function sendJson<T>(
  url: string,
  method: "POST" | "PATCH" | "DELETE",
  key: string,
  input?: unknown,
): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: input === undefined ? { Accept: "application/json" } : JSON_HEADERS,
      credentials: "same-origin",
      ...(input === undefined ? {} : { body: JSON.stringify(input) }),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (res.status === 204) return { ok: true, value: null as T };
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!body?.[key]) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: body[key] as T };
}

// ── Производственный календарь ──────────────────────────────────────────────────

export function fetchCalendar(): Promise<ApiResult<CalendarDay[]>> {
  return getJson<CalendarDay[]>("/api/references/calendar", "days");
}
export function createCalendarDay(input: {
  date: string;
  type: CalendarDayType;
  note?: string;
}): Promise<ApiResult<CalendarDay>> {
  return sendJson<CalendarDay>("/api/references/calendar", "POST", "day", input);
}
export function updateCalendarDay(
  id: string,
  input: { type?: CalendarDayType; note?: string },
): Promise<ApiResult<CalendarDay>> {
  return sendJson<CalendarDay>(
    `/api/references/calendar/${encodeURIComponent(id)}`,
    "PATCH",
    "day",
    input,
  );
}
export function deleteCalendarDay(id: string): Promise<ApiResult<null>> {
  return sendJson<null>(`/api/references/calendar/${encodeURIComponent(id)}`, "DELETE", "day");
}

// ── Ключевая ставка ЦБ ──────────────────────────────────────────────────────────

export function fetchKeyRates(): Promise<ApiResult<KeyRate[]>> {
  return getJson<KeyRate[]>("/api/references/key-rates", "rates");
}
export function createKeyRate(input: {
  from: string;
  rate: number;
  note?: string;
}): Promise<ApiResult<KeyRate>> {
  return sendJson<KeyRate>("/api/references/key-rates", "POST", "rate", input);
}
export function updateKeyRate(
  id: string,
  input: { rate?: number; note?: string },
): Promise<ApiResult<KeyRate>> {
  return sendJson<KeyRate>(
    `/api/references/key-rates/${encodeURIComponent(id)}`,
    "PATCH",
    "rate",
    input,
  );
}
export function deleteKeyRate(id: string): Promise<ApiResult<null>> {
  return sendJson<null>(`/api/references/key-rates/${encodeURIComponent(id)}`, "DELETE", "rate");
}
